import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { AgGridReact } from "ag-grid-react";
import { ProjectContext } from "context/ProjectProvider";
import { getFeatureEntityLog } from "services/feature-apis";
import { FeatureTypes } from "entity-app/constants/index";
import featureDateBlockColumns, {
  getMaxNumberOfMilestonesFromEntityList
} from "entity-app/shared-components/entity-milestones-duration-columns/feature-date-block-columns";
import { useMaterialLogAgGrid } from "pages/material-log-feature/hooks/material-ag-grid-data";
import { fetchCommentsByMaterialId } from "pages/material-log-feature/material-comments";
import { TMaterialLog } from "pages/material-log-feature/model";
import AgGridPrintLayout from "./ag-grid-print-layout";
import { MaterialLogPrintViewState } from "./model";

function MaterialLogPrintView({ state }: { state: MaterialLogPrintViewState }) {
  const {
    columnHeaders: { materialHeaderMap },
    tokenRetrievalState,
    isFeatureFlagEnabled
  } = useContext(ProjectContext);

  const gridRef = useRef<AgGridReact<any>>(null);
  const [rowData, setRowData] = useState<any[]>();
  const [rowLoading, setRowLoading] = useState(true);

  const fetchLogData = useCallback(async () => {
    setRowLoading(true);
    const [res, commentsById] = await Promise.all([
      getFeatureEntityLog({
        projectToken: tokenRetrievalState?.token,
        featureType: FeatureTypes.MATERIALS
      }),
      state.includeComments
        ? fetchCommentsByMaterialId(tokenRetrievalState?.token)
        : null
    ]);

    if (res.success) {
      setRowData(
        commentsById
          ? res.data.map((row: TMaterialLog) => ({
              ...row,
              comments: commentsById.get(row.id) ?? []
            }))
          : [...res.data]
      );
    }
    setRowLoading(false);
  }, [tokenRetrievalState?.token, state.includeComments]);

  useEffect(() => {
    if (tokenRetrievalState?.token) {
      fetchLogData();
    }
    return () => {};
  }, [fetchLogData, tokenRetrievalState?.token]);

  const maxNumberOfMilestones = useMemo(() => {
    return getMaxNumberOfMilestonesFromEntityList(rowData || null);
  }, [rowData]);

  const milestonesColumns = useMemo(
    () =>
      featureDateBlockColumns(
        maxNumberOfMilestones?.milestonesCount ?? null,
        maxNumberOfMilestones?.lastMilestoneHasOffset ?? false,
        maxNumberOfMilestones?.milestoneIndicesWithOffset
      ),
    [
      maxNumberOfMilestones?.lastMilestoneHasOffset,
      maxNumberOfMilestones?.milestonesCount,
      maxNumberOfMilestones?.milestoneIndicesWithOffset
    ]
  );

  const { columnDefs, autoGroupColumnDef } = useMaterialLogAgGrid({
    featureKey: "materials",
    milestonesColumns,
    cannotWriteAccessOfMaterial: true,
    materialHeaderMap,
    isLBSFeatureEnabled: false,
    canSetLBSForMaterial: false,
    isMaterialCsiClassificationEnabled: isFeatureFlagEnabled(
      "MATERIAL_CSI_CLASSIFICATION"
    )
  });

  return (
    <AgGridPrintLayout
      gridRef={gridRef}
      state={state}
      columnDefEntity={columnDefs}
      autoGroupColumnDef={autoGroupColumnDef}
      rowData={rowData}
      rowLoading={rowLoading}
    />
  );
}

export default MaterialLogPrintView;
